import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import logo from "../../assets/Arconest_logo.png";
import google from "../../assets/google.png";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";

const OtpLogin = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Testimonials data for carousel
  const testimonials = [
    {
      id: 1,
      text: "Testimonials Content goes here Testimonials Content goes here Testimonials Content goes here. Testimonials Content goes here.",
      name: "Nikhil M",
      role: "Founder & CEO",
    },
    {
      id: 2,
      text: "User experiences shared here User experiences shared here User experiences shared here. User experiences shared here.",
      name: "Jac Watson",
      role: "Assistant Manager",
    },
    {
      id: 3,
      text: "Customer feedback appears here Customer feedback appears here Customer feedback appears here. Customer feedback appears here.",
      name: "Malee Rathod",
      role: "Senior Supervisor",
    },
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleNext = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setCurrentTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length,
    );
  };

  // OTP States
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { verifyOtp, signInWithGoogle } = useAuth();

  // Refs for OTP input fields
  const otpRefs = useRef([]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Format timer display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handle mobile number change
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setMobileNumber(value);
      setError("");
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }

      if (!value && index > 0) {
        otpRefs.current[index - 1]?.focus();
      }
    }
  };

  // Handle OTP input key events
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pasteData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pasteData.length && i < 6; i++) {
        newOtp[i] = pasteData[i];
      }
      setOtp(newOtp);

      const lastFilledIndex = Math.min(pasteData.length - 1, 5);
      otpRefs.current[lastFilledIndex]?.focus();
    }
  };

  // Handle Send OTP
  const handleSendOtp = async () => {
    if (!mobileNumber || mobileNumber.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(`${url}/api/auth/send-otp`, {
        phone: `91${mobileNumber}`,
      });

      if (response.data.success) {
        setIsOtpSent(true);
        setTimer(90);
        setSuccess("OTP sent successfully");

        setTimeout(() => {
          otpRefs.current[0]?.focus();
        }, 100);
      } else {
        setError(response.data.message || "Failed to send OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Handle Resend OTP
  const handleResendOtp = async () => {
    if (timer > 0) return;

    setResendLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(`${url}/api/auth/send-otp`, {
        phone: `91${mobileNumber}`,
      });

      if (response.data.success) {
        setTimer(90);
        setOtp(Array(6).fill(""));
        setSuccess("OTP resent successfully");

        setTimeout(() => {
          otpRefs.current[0]?.focus();
        }, 100);
      } else {
        setError(response.data.message || "Failed to resend OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setResendLoading(false);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      setError("Please enter complete 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const result = await verifyOtp(mobileNumber, enteredOtp);

      if (result.success) {
        if (result.exists) {
          setSuccess("Login successful! Redirecting to dashboard...");
          setTimeout(() => {
            navigate("/dashboard");
          }, 1500);
        } else {
          setSuccess("Please complete your registration");

          navigate("/register", {
            state: {
              phone: result.phone,
              fromOtp: true,
            },
          });
        }
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setError(err.message || "Invalid OTP");
      setOtp(Array(6).fill(""));
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Login
  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError("");

      const result = await signInWithGoogle();

      if (result.exists) {
        setSuccess("Google login successful! Redirecting to dashboard...");
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        setSuccess("No account found. Please complete your registration.");
        setTimeout(() => {
          navigate("/register", {
            state: {
              email: result.firebaseUser.email,
              name: result.firebaseUser.displayName,
              isGoogleLogin: true,
              role: result.role || null,
            },
          });
        }, 1500);
      }
    } catch (error) {
      console.error("Google login error:", error);
      setError("Failed to sign in with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit mobile number
  const handleEditMobile = () => {
    setIsOtpSent(false);
    setTimer(0);
    setOtp(Array(6).fill(""));
    setError("");
    setSuccess("");
  };

  // Handle registration as Engineer or Client
  const handleRegisterAs = (role) => {
    navigate("/register", {
      state: {
        role: role,
        fromOtp: true,
        phone: mobileNumber ? `91${mobileNumber}` : null,
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: COLORS.BACKGROUND,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccess("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* ================= HEADER ================= */}
      <Box
        sx={{
          px: { xs: 2, md: 6 },
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {/* LOGO */}
        <Box display="flex" alignItems="center">
          <Box
            component="img"
            src={logo}
            alt="Arconest Consultants Logo"
            sx={{
              height: { xs: 32, md: 40 },
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Start As */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              fontFamily: FONT_FAMILY,
              color: COLORS.TEXT,
              fontSize: { xs: "0.875rem", md: "1rem" },
              whiteSpace: "nowrap",
            }}
          >
            Register As:
          </Typography>

          <Button
            onClick={() => handleRegisterAs("ENGINEER")}
            variant="contained"
            size="small"
            sx={{
              bgcolor: COLORS.PRIMARY,
              borderRadius: 1,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              ...TYPOGRAPHY.H4,
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              px: { xs: 1, md: 1.5 },
              py: { xs: 0.5, md: 0.75 },
              minWidth: { xs: "80px", md: "100px" },
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: COLORS.PRIMARY,
                opacity: 0.9,
              },
            }}
          >
            An Engineer
          </Button>

          <Button
            variant="contained"
            onClick={() => handleRegisterAs("CLIENT")}
            size="small"
            sx={{
              bgcolor: COLORS.SECONDARY,
              borderRadius: 1,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              ...TYPOGRAPHY.H4,
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              px: { xs: 1, md: 1.5 },
              py: { xs: 0.5, md: 0.75 },
              minWidth: { xs: "70px", md: "90px" },
              whiteSpace: "nowrap",
              "&:hover": {
                bgcolor: COLORS.SECONDARY,
                opacity: 0.9,
              },
            }}
          >
            A Client
          </Button>
        </Box>
      </Box>

      {/* ================= MAIN CONTENT ================= */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          px: { xs: 2, md: 6 },
          py: { xs: 3, md: 4 },
          boxSizing: "border-box",
        }}
      >
        {/* ================= LEFT COLUMN ================= */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: "column",
            pr: { md: 4 },
            mb: { xs: 4, md: 0 },
            position: "relative",
          }}
        >
          <Typography
            sx={{
              ...TYPOGRAPHY.H2,
              color: COLORS.SECONDARY,
              maxWidth: 420,
              mb: { xs: 3, md: 4 },
              textAlign: { xs: "center", md: "left" },
              width: "100%",
              mx: "auto",
            }}
          >
            #1 Web Application for Managing Client Projects
          </Typography>

          {/* Empty Space (same as in register screen) */}
          <Box
            sx={{
              height: {
                xs: "180px",
                sm: "220px",
                md: "280px",
                lg: "320px",
              },
              mb: { xs: 3, md: 4 },
              width: "100%",
            }}
          />

          {/* Testimonials Carousel */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { md: "90%" },
              mt: { xs: -2, md: -8 },
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                minHeight: "140px",
                mb: 1,
              }}
            >
              {testimonials.map((testimonial, index) => (
                <Box
                  key={testimonial.id}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: index === currentTestimonial ? 1 : 0,
                    transition: "opacity 0.5s ease-in-out",
                    pointerEvents:
                      index === currentTestimonial ? "auto" : "none",
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      opacity: 0.8,
                      mb: { xs: 1.5, md: 2 },
                      textAlign: { xs: "center", md: "left" },
                      lineHeight: 1.6,
                    }}
                  >
                    {testimonial.text}
                  </Typography>

                  {/* Founder Section */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: { xs: "center", md: "flex-start" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: { xs: 24, md: 30 },
                        height: { xs: 24, md: 30 },
                      }}
                    />
                    <Box>
                      <Typography
                        component="div"
                        sx={{
                          ...TYPOGRAPHY.H4,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.PRIMARY,
                          fontWeight: 600,
                        }}
                      >
                        <strong>{testimonial.name}</strong>
                      </Typography>
                      <Typography
                        component="div"
                        sx={{
                          ...TYPOGRAPHY.CAPTION_MICRO,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                        }}
                      >
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Carousel Controls */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mt: { xs: -3, md: -5 },
              }}
            >
              <IconButton
                onClick={handlePrev}
                size="small"
                sx={{
                  color: COLORS.PRIMARY,
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                  padding: 0.5,
                }}
              >
                <KeyboardArrowLeft fontSize="small" />
              </IconButton>

              {/* Dots Indicator */}
              <Box display="flex" gap={1}>
                {testimonials.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor:
                        index === currentTestimonial
                          ? COLORS.PRIMARY
                          : COLORS.LIGHT_SECONDARY,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Box>

              <IconButton
                onClick={handleNext}
                size="small"
                sx={{
                  color: COLORS.PRIMARY,
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                  padding: 0.5,
                }}
              >
                <KeyboardArrowRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* ================= RIGHT COLUMN ================= */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: "column",
            pl: { md: 4 },
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          {/* Login Form Container - FIXED LAYOUT */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "400px" },
              display: "flex",
              flexDirection: "column",
              minHeight: "100%",
              alignItems: "center",
              // Create space at the bottom for alignment
              position: "relative",
              minHeight: { md: "500px" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                maxWidth: "400px",
                // Normal flow for mobile, special layout for desktop
                flex: 1,
                justifyContent: { md: "space-between" },
              }}
            >
              {/* Top Section: Title and Form Fields */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H2,
                    color: COLORS.SECONDARY,
                    mb: 3,
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  Login with OTP
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    width: "100%",
                    alignItems: "center",
                    maxWidth: "294px",
                  }}
                >
                  {/* Mobile Number Section */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        mb: "6px",
                        color: COLORS.PRIMARY,
                        fontWeight: 500,
                        width: "100%",
                        textAlign: "left",
                      }}
                    >
                      Mobile No*
                    </Typography>

                    <TextField
                      fullWidth
                      placeholder="Enter Mobile Number(Whatsapp)"
                      value={mobileNumber}
                      onChange={handleMobileChange}
                      disabled={isOtpSent || loading}
                      inputProps={{
                        maxLength: 10,
                        inputMode: "numeric",
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 50,
                          borderRadius: "7px",
                          width: "100%",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: COLORS.PRIMARY,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: COLORS.PRIMARY,
                            borderWidth: "1px",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          color: COLORS.PRIMARY,
                          fontFamily: FONT_FAMILY,
                          fontSize: "16px",
                          padding: "12.5px 14px",
                          "&::placeholder": {
                            color: COLORS.LIGHT_SECONDARY,
                            opacity: 1,
                          },
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: COLORS.LIGHT_SECONDARY,
                          borderWidth: "0.2px",
                        },
                      }}
                    />
                  </Box>

                  {/* Send OTP Button - Only show if OTP not sent */}
                  {!isOtpSent && (
                    <Button
                      onClick={handleSendOtp}
                      disabled={loading || mobileNumber.length !== 10}
                      sx={{
                        width: "100%",
                        height: 50,
                        bgcolor: COLORS.SECONDARY,
                        color: "#fff",
                        borderRadius: "7px",
                        textTransform: "none",
                        fontFamily: FONT_FAMILY,
                        ...TYPOGRAPHY.H4,
                        "&:hover": {
                          bgcolor: COLORS.PRIMARY,
                        },
                        "&:disabled": {
                          opacity: 0.7,
                          bgcolor: COLORS.LIGHT_SECONDARY,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Send OTP"
                      )}
                    </Button>
                  )}

                  {/* OTP Section - Only show if OTP is sent */}
                  {isOtpSent && (
                    <>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.H4,
                            mb: "6px",
                            color: COLORS.PRIMARY,
                            mt: 3,
                            fontWeight: 500,
                            width: "100%",
                            textAlign: "left",
                          }}
                        >
                          Enter OTP*
                        </Typography>

                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="center"
                          sx={{ width: "100%" }}
                        >
                          {Array.from({ length: 6 }).map((_, index) => (
                            <TextField
                              key={index}
                              inputRef={(el) => (otpRefs.current[index] = el)}
                              placeholder="0"
                              value={otp[index]}
                              onChange={(e) =>
                                handleOtpChange(index, e.target.value)
                              }
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              onPaste={index === 0 ? handleOtpPaste : undefined}
                              disabled={loading}
                              inputProps={{
                                maxLength: 1,
                                inputMode: "numeric",
                              }}
                              sx={{
                                width: { xs: 40, md: 50 },
                                "& .MuiOutlinedInput-root": {
                                  height: 50,
                                  borderRadius: "7px",
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: COLORS.PRIMARY,
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: COLORS.PRIMARY,
                                      borderWidth: "1px",
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                  textAlign: "center",
                                  fontSize: { xs: 16, md: 18 },
                                  color: COLORS.PRIMARY,
                                  fontFamily: FONT_FAMILY,
                                  fontWeight: 500,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: COLORS.LIGHT_SECONDARY,
                                  borderWidth: "0.2px",
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Timer and Resend Section */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexDirection={{ xs: "column", md: "row" }}
                        gap={{ xs: 1, md: 0 }}
                        sx={{ width: "100%" }}
                      >
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.BODY_STANDARD,
                            color: COLORS.TEXT,
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                            textAlign: { xs: "center", md: "left" },
                          }}
                        >
                          {timer > 0 ? (
                            <>
                              OTP not received? Resend OTP in{" "}
                              <span
                                style={{
                                  color: COLORS.SECONDARY,
                                  fontWeight: "bold",
                                }}
                              >
                                {formatTime(timer)}
                              </span>
                            </>
                          ) : (
                            "Didn't receive OTP?"
                          )}
                        </Typography>

                        {timer === 0 && (
                          <Button
                            onClick={handleResendOtp}
                            disabled={resendLoading}
                            sx={{
                              textTransform: "none",
                              color: COLORS.PRIMARY,
                              minWidth: "auto",
                              padding: "6px 12px",
                              borderRadius: "4px",
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.BODY_STANDARD,
                              fontSize: { xs: "0.75rem", md: "0.875rem" },
                              "&:hover": {
                                backgroundColor: "rgba(106, 17, 203, 0.1)",
                              },
                            }}
                          >
                            {resendLoading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              "Resend OTP"
                            )}
                          </Button>
                        )}
                      </Box>

                      {/* Verify Button */}
                      <Button
                        onClick={handleVerifyOtp}
                        disabled={loading || otp.some((digit) => !digit)}
                        sx={{
                          width: "100%",
                          height: 50,
                          bgcolor: COLORS.SECONDARY,
                          color: "#fff",
                          borderRadius: "7px",
                          textTransform: "none",
                          fontFamily: FONT_FAMILY,
                          ...TYPOGRAPHY.H4,
                          fontWeight: 500,
                          "&:hover": {
                            bgcolor: COLORS.PRIMARY,
                          },
                          "&:disabled": {
                            opacity: 0.7,
                            bgcolor: COLORS.LIGHT_SECONDARY,
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Verify OTP & Continue"
                        )}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              {/* Bottom Section: Google Login and Footer - Fixed Position for Desktop */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: { xs: 3, md: "auto" }, // Auto margin on desktop pushes it to bottom
                  mb: { xs: 0, md: 0 },
                }}
              >
                {/* Divider with "Or" and Google Login */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    maxWidth: "294px",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      my: 2,
                    }}
                  >
                    <Divider
                      sx={{
                        flex: 1,
                        borderColor: COLORS.LIGHT_SECONDARY,
                        borderWidth: "0.2px",
                      }}
                    />
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mx: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Or
                    </Typography>
                    <Divider
                      sx={{
                        flex: 1,
                        borderColor: COLORS.LIGHT_SECONDARY,
                        borderWidth: "0.2px",
                      }}
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={
                      <Box
                        component="img"
                        src={google}
                        alt="Google"
                        sx={{ width: 20, height: 20 }}
                      />
                    }
                    onClick={handleGoogleLogin}
                    disabled={loading || resendLoading}
                    sx={{
                      width: "100%",
                      height: 50,
                      borderRadius: "7px",
                      borderWidth: "0.2px",
                      textTransform: "none",
                      fontFamily: FONT_FAMILY,
                      color: COLORS.PRIMARY,
                      borderColor: COLORS.LIGHT_SECONDARY,
                      ...TYPOGRAPHY.H4,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(106, 17, 203, 0.04)",
                        borderColor: COLORS.PRIMARY,
                      },
                      "&:disabled": {
                        opacity: 0.5,
                        borderColor: COLORS.LIGHT_SECONDARY,
                      },
                    }}
                  >
                    Login with Google
                  </Button>
                </Box>

                {/* Footer Text - Now properly below Google button */}
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    opacity: 0.6,
                    textAlign: "center",
                    color: COLORS.TEXT,
                    mt: 3,
                    width: "100%",
                  }}
                >
                  Powered by Arconest Consultants
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OtpLogin;
